import { forwardRef } from 'react'
import { useFormContext } from 'react-hook-form'
import get from 'lodash/get'
import { Combobox } from '@klear/ui/components/combobox'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { cn } from '@klear/ui/lib/utils'
import { DEFAULT_CURRENCY_CODE, CURRENCY_CODE_OPTIONS, type CurrencyCode } from '@klear/constants/currency'
import { Money } from '@klear/money'
import { moneyFromMinorDigits } from '@klear/ui/components/money-form-value'
import { moneyFromFormValue } from '@klear/ui/components/money-form-value'
import { clampMoneyToMax } from '@klear/ui/components/money-input'

export interface FormMoneyInputProps {
  name: string
  label?: string
  isEditing?: boolean
  disabled?: boolean
  placeholder?: string
  className?: string
  helperText?: string
  max?: Money
}

const FormMoneyInput = forwardRef<HTMLInputElement, FormMoneyInputProps>(function FormMoneyInput(
  { name, label = 'Amount', isEditing = true, disabled = false, placeholder = '0.00', helperText, className, max },
  ref
) {
  const {
    watch,
    setValue,
    formState: { errors }
  } = useFormContext()

  const raw = watch(name)
  const money = moneyFromFormValue(raw)
  const fieldError = get(errors, name)
  const errorMessage = fieldError?.message as string | undefined
  const editable = isEditing && !disabled
  const currency = money?.currency ?? DEFAULT_CURRENCY_CODE

  return (
    <div className={cn('space-y-2', className)}>
      <Label className="flex items-center gap-2">{label}</Label>
      <div className="flex">
        <Input
          ref={ref}
          type="text"
          className="rounded-r-none border-r-0"
          placeholder={placeholder}
          disabled={!editable}
          value={money?.toString() ?? ''}
          onChange={(e) => {
            const digits = e.target.value.replace(/[^\d]/g, '')
            if (digits === '') {
              setValue(name, undefined, { shouldValidate: true, shouldDirty: true })
              return
            }
            const next = moneyFromMinorDigits(digits, currency)
            setValue(name, clampMoneyToMax(next, max), { shouldValidate: true, shouldDirty: true })
          }}
        />
        <Combobox<CurrencyCode>
          className="w-[88px] shrink-0 rounded-l-none border-l-0"
          value={currency}
          options={CURRENCY_CODE_OPTIONS}
          placeholder="CCY"
          disabled={!editable}
          onChange={(next) => {
            const nextCur = (next === '' ? DEFAULT_CURRENCY_CODE : next) as CurrencyCode
            const minor = money?.amount ?? 0n
            const nextMoney = Money.fromMinorUnits(minor, nextCur)
            setValue(name, clampMoneyToMax(nextMoney, max), { shouldValidate: true, shouldDirty: true })
          }}
          error={fieldError}
        />
      </div>
      {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
      {helperText ? <p className="text-sm text-muted-foreground">{helperText}</p> : null}
    </div>
  )
})

FormMoneyInput.displayName = 'FormMoneyInput'

export default FormMoneyInput
